import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../hooks";
import { requestPasswordReset, resetPassword } from "../../../slices/authSlice";
import { toast } from "react-toastify";
import Slideshow from "../../../components/Slideshow";
import { slides } from "../../../assets/slideShowData";
import { logo } from "../../../assets/exports";
import { Eye, EyeClosed, ArrowLeft, MailCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ForgotPasswordPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Step 1: request code ──────────────────────────────────────────────────
  const emailForm = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Enter a valid email").required("Email is required"),
    }),
    onSubmit: async ({ email }, { setSubmitting }) => {
      try {
        await dispatch(requestPasswordReset({ email })).unwrap();
        setSubmittedEmail(email);
        setStep(2);
      } catch (err: any) {
        toast.error(err || "Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ── Step 2: verify code + set new password ────────────────────────────────
  const resetForm = useFormik({
    initialValues: { code: "", newPassword: "", confirmPassword: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .length(6, "Code must be exactly 6 digits")
        .matches(/^\d+$/, "Code must be numeric")
        .required("Reset code is required"),
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async ({ code, newPassword }, { setSubmitting }) => {
      try {
        await dispatch(resetPassword({ email: submittedEmail, code, newPassword })).unwrap();
        toast.success("Password reset successfully. Please log in.");
        navigate("/login");
      } catch (err: any) {
        toast.error(err || "Invalid or expired code. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-screen lg:flex-row lg:gap-4 lg:justify-between text-left flex items-center justify-center p-1">
      <div className="hidden lg:block lg:w-1/2">
        <Slideshow slides={slides} />
      </div>

      <div className="flex w-full lg:w-1/2 items-center justify-center lg:p-4">
        <div className="w-full bg-white rounded-2xl md:shadow-md p-6 mx-auto sm:max-w-sm md:max-w-md lg:max-w-md">

          {/* Logo */}
          <div className="flex flex-col items-center my-5 px-2">
            <img
              src={logo}
              className="lg:w-50 lg:h-50 w-60 h-60 object-contain cursor-pointer"
              alt="StorePilot Logo"
              onClick={() => navigate("/")}
            />
          </div>

          {step === 1 ? (
            <>
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-gray-800">Forgot your password?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Enter your email address and we'll send you a 6-digit reset code.
                </p>
              </div>

              <form onSubmit={emailForm.handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Email address</label>
                  <input
                    name="email"
                    type="email"
                    value={emailForm.values.email}
                    onChange={emailForm.handleChange}
                    onBlur={emailForm.handleBlur}
                    placeholder="you@example.com"
                    className={`form-input ${emailForm.touched.email && emailForm.errors.email ? "border-red-500" : "border-gray-200"}`}
                  />
                  {emailForm.touched.email && emailForm.errors.email && (
                    <p className="text-xs text-red-500 mt-1">{emailForm.errors.email}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={emailForm.isSubmitting}
                  className="form-btn"
                >
                  {emailForm.isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <span className="loader" />
                      <span>Sending code...</span>
                    </div>
                  ) : (
                    "Send Reset Code"
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success banner */}
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3 mb-5">
                <MailCheck size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">Code sent!</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    We sent a 6-digit code to <strong>{submittedEmail}</strong>. It expires in 15 minutes.
                  </p>
                </div>
              </div>

              <form onSubmit={resetForm.handleSubmit} className="space-y-4">
                {/* Code */}
                <div>
                  <label className="form-label">Reset code</label>
                  <input
                    name="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={resetForm.values.code}
                    onChange={resetForm.handleChange}
                    onBlur={resetForm.handleBlur}
                    placeholder="123456"
                    className={`form-input tracking-[0.5em] text-center font-mono text-lg ${resetForm.touched.code && resetForm.errors.code ? "border-red-500" : "border-gray-200"}`}
                  />
                  {resetForm.touched.code && resetForm.errors.code && (
                    <p className="text-xs text-red-500 mt-1">{resetForm.errors.code}</p>
                  )}
                </div>

                {/* New password */}
                <div className="relative">
                  <label className="form-label">New password</label>
                  <input
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={resetForm.values.newPassword}
                    onChange={resetForm.handleChange}
                    onBlur={resetForm.handleBlur}
                    placeholder="••••••••"
                    className={`form-input ${resetForm.touched.newPassword && resetForm.errors.newPassword ? "border-red-500" : "border-gray-200"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="show-password"
                  >
                    {showPassword ? <EyeClosed /> : <Eye />}
                  </button>
                  {resetForm.touched.newPassword && resetForm.errors.newPassword && (
                    <p className="text-xs text-red-500 mt-1">{resetForm.errors.newPassword}</p>
                  )}
                </div>

                {/* Confirm password */}
                <div className="relative">
                  <label className="form-label">Confirm new password</label>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={resetForm.values.confirmPassword}
                    onChange={resetForm.handleChange}
                    onBlur={resetForm.handleBlur}
                    placeholder="••••••••"
                    className={`form-input ${resetForm.touched.confirmPassword && resetForm.errors.confirmPassword ? "border-red-500" : "border-gray-200"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="show-password"
                  >
                    {showConfirmPassword ? <EyeClosed /> : <Eye />}
                  </button>
                  {resetForm.touched.confirmPassword && resetForm.errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">{resetForm.errors.confirmPassword}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={resetForm.isSubmitting}
                  className="form-btn"
                >
                  {resetForm.isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <span className="loader" />
                      <span>Resetting...</span>
                    </div>
                  ) : (
                    "Reset Password"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary mt-1"
                >
                  <ArrowLeft size={14} /> Use a different email
                </button>
              </form>
            </>
          )}

          <footer className="mt-6 text-sm text-gray-500">
            <a href="/login" className="flex items-center gap-1 text-primary hover:underline">
              <ArrowLeft size={14} /> Back to login
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
